import { OpenApiError } from '@zyno-io/openapi-client-codegen';

/**
 * Client-side email checks for any address the API will store or send mail to.
 *
 * This is a shape check only — it blocks the typos that cost us a bounced email (`name@gmailxom`,
 * `name@gmailcom`, `name@gmail.c`), not every undeliverable address. Deliverability (MX lookup) is the
 * server's call; the browser can't do DNS and must not guess. Nothing here autocorrects: the customer
 * fixes the address they meant.
 *
 * Kept free of `@/global` and vue-foundation imports — the public CX forms bundle and the shared API
 * client setup use it (`bundle-isolation.spec.ts`).
 */

// Local part: anything but whitespace and `@`. Domain: one or more dot-separated labels, then a TLD of
// at least two letters (or a punycode TLD, `xn--…`). The required dot is what catches `gmailxom`.
// Letters are Unicode-aware: `user@münchen.de` is a real address, and a plain text input hands it over
// un-punycoded.
const EMAIL_RE = /^[^\s@]+@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+(?:\p{L}{2,}|xn--[A-Za-z0-9-]+)$/u;

export const INVALID_EMAIL_MESSAGE = 'Enter a valid email address, like name@example.com.';
export const INCOMPLETE_EMAIL_DOMAIN_MESSAGE = 'Check the part after the @ — it looks like a dot is missing (e.g. gmail.com).';

/** The value to validate and send: trimmed. Never otherwise rewritten. */
export function normalizeEmail(value: string | null | undefined): string {
    return (value ?? '').trim();
}

export function isValidEmailAddress(value: string | null | undefined): boolean {
    return EMAIL_RE.test(normalizeEmail(value));
}

/**
 * The field error for `value`, or `undefined` when it's fine. A blank value is not this check's
 * business — whether the field is required is up to the form.
 */
export function emailAddressError(value: string | null | undefined): string | undefined {
    const email = normalizeEmail(value);
    if (!email || EMAIL_RE.test(email)) return undefined;

    // The common typo — a domain with no dot at all — gets a message that points at it.
    const at = email.indexOf('@');
    const domain = email.slice(at + 1);
    const wellFormedOtherwise = at > 0 && !/[\s@]/.test(domain) && !/\s/.test(email);
    return wellFormedOtherwise && domain && !domain.includes('.') ? INCOMPLETE_EMAIL_DOMAIN_MESSAGE : INVALID_EMAIL_MESSAGE;
}

/** The email rejections the API reports against a field. */
export type EmailApiErrorCode = 'invalid_email' | 'email_domain_unmailable' | 'email_domain_lookup_failed';

export interface EmailApiFieldError {
    code: EmailApiErrorCode;
    /** What to show on the field. */
    message: string;
    /** The field the API named, if it named one (a body path such as `email` or a form field id/alias). */
    field?: string;
    /**
     * `email_domain_lookup_failed` is a DNS timeout/SERVFAIL on our side, not a bad address: the same
     * address may well succeed on the next attempt, so the form must stay submittable.
     */
    retryable: boolean;
}

export const EMAIL_API_ERROR_MESSAGES: Record<EmailApiErrorCode, string> = {
    invalid_email: INVALID_EMAIL_MESSAGE,
    email_domain_unmailable: "This email address's domain can't receive email. Check the address for typos.",
    email_domain_lookup_failed: "We couldn't verify this email address's domain right now. Please try again."
};

const EMAIL_API_ERROR_MESSAGE_TEMPLATES = [
    '"{email}" is not a valid email address. Enter an address like name@example.com.',
    'The domain "{domain}" can\'t receive email. Check the address for typos.',
    'We couldn\'t verify the domain "{domain}" right now. Please try again.'
];

/** Translate a stable sentence before inserting the API's address/domain as plain text. */
export function translateEmailApiErrorMessage(message: string, translate: (text: string) => string): string {
    const translated = translate(message);
    if (translated !== message) return translated;

    for (const template of EMAIL_API_ERROR_MESSAGE_TEMPLATES) {
        const placeholder = template.includes('{email}') ? '{email}' : '{domain}';
        const [prefix, suffix] = template.split(placeholder);
        if (message.length < prefix.length + suffix.length || !message.startsWith(prefix) || !message.endsWith(suffix)) continue;

        const value = message.slice(prefix.length, message.length - suffix.length);
        // A callback keeps replacement tokens (e.g. "$&" in a local part) literal.
        return translate(template).replace(placeholder, () => value);
    }
    return message;
}

function isEmailApiErrorCode(code: unknown): code is EmailApiErrorCode {
    return typeof code === 'string' && code in EMAIL_API_ERROR_MESSAGES;
}

function fieldNameOf(entry: Record<string, unknown>): string | undefined {
    for (const key of ['field', 'path', 'fieldId', 'property']) {
        const value = entry[key];
        if (typeof value === 'string' && value) return value;
        if (Array.isArray(value) && value.length) return value.join('.');
    }
    return undefined;
}

/**
 * The email rejection an API error carries, or `undefined` if it isn't one.
 *
 * Every configured API client converts these into a `UserError` with the rejection attached
 * (`configureZynoOpenApiClient`), so a plain `handleErrorAndAlert` already shows the message — this is
 * only for callers that want to put it on a field instead. It also reads a raw `OpenApiError`, which
 * is what that conversion uses.
 */
export function emailApiFieldError(err: unknown): EmailApiFieldError | undefined {
    const attached = (err as { emailRejection?: EmailApiFieldError } | null | undefined)?.emailRejection;
    if (attached) return attached;
    return readEmailApiFieldError(err);
}

/**
 * Accepts the field-error forms the platform's services use — a top-level `{ error, code, field }`,
 * or an `errors` / `fieldErrors` list (or field-keyed map) of `{ code, field|path, message }` — so a
 * caller only ever deals with the three codes.
 *
 * Only an `OpenApiError` carries a body. vue-foundation turns a 422 `{ error }` into a bare `UserError`
 * first, so a 422 rejection loses its code: its message is still shown, but not on a field.
 */
function readEmailApiFieldError(err: unknown): EmailApiFieldError | undefined {
    if (!(err instanceof OpenApiError)) return undefined;
    const body = err.body;
    if (!body || typeof body !== 'object') return undefined;

    const candidates: Record<string, unknown>[] = [body as Record<string, unknown>];
    const { error, errors, fieldErrors } = body as Record<string, unknown>;
    if (error && typeof error === 'object') candidates.push(error as Record<string, unknown>);
    for (const list of [errors, fieldErrors]) {
        if (Array.isArray(list)) {
            for (const entry of list) if (entry && typeof entry === 'object') candidates.push(entry as Record<string, unknown>);
        } else if (list && typeof list === 'object') {
            for (const [field, entry] of Object.entries(list)) {
                if (typeof entry === 'string') candidates.push({ field, code: entry });
                else if (entry && typeof entry === 'object') candidates.push({ field, ...(entry as Record<string, unknown>) });
            }
        }
    }

    for (const entry of candidates) {
        const code = entry.code;
        if (!isEmailApiErrorCode(code)) continue;
        const message = [entry.message, entry.error, error].find((value): value is string => typeof value === 'string' && !!value.trim());
        return {
            code,
            message: message ?? EMAIL_API_ERROR_MESSAGES[code],
            field: fieldNameOf(entry),
            retryable: code === 'email_domain_lookup_failed'
        };
    }
    return undefined;
}
